import React, { useState, useEffect } from 'react';
import { postRequest } from '../../../utils/request';

// Hook to use debounce
function useDebounce(value, delay) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );

  return debouncedValue;
}


function fetchList(api, query, setOptions, setSearching) {
  return postRequest(api, { query }).then(result => {
    // console.log(result.data, 'the result');
    setOptions(result.data ? result.data.results : result.results);
    setSearching(false);
  });
}

function toggleSelection(selection, selected, setSelected, selectedIds, setSelectedIds, handleChange) {
  if (selectedIds.indexOf(selection.id) !== -1) {
    // We are UNSELECTING!
    const newSelected = selected.filter(item => item.id !== selection.id);
    setSelected(newSelected);
    const newSelectedIds = selectedIds.filter(itemId => itemId !== selection.id);
    setSelectedIds(newSelectedIds);
    handleChange(newSelected);
  }
  else {
    // We are SELECTING!
    const newSelected = [
      ...selected,
      { ...selection },
    ];
    setSelected(newSelected);
    const newSelectedIds = [
      ...selectedIds,
      selection.id
    ];
    setSelectedIds(newSelectedIds);
    handleChange(newSelected);
  }
}

function SelectRow({ name, subtext, active, remove, toggle }) {
  const iconBg = active ? 'bg-green-400' : 'bg-gray-400';
  const iconClass = `h-2 w-2 m-2 rounded-full ${iconBg}`;

  return (
    <div onClick={toggle} className={`flex justify-start text-gray-700 ${!remove ? 'cursor-pointer hover:text-blue-400 hover:bg-blue-100' : ''} rounded-md px-2 py-1 my-1`}>
      {!remove && <span className={iconClass}></span>}
      <div className="flex-grow font-medium px-2">
        {name}
        {subtext && <div className="text-sm font-normal text-gray-500 tracking-wide">{subtext}</div>}
      </div>
      {remove && (
        <button onClick={remove} className="bg-transparent hover:bg-red-500 text-red-700 font-semibold hover:text-white py-2 px-4 border border-red-500 hover:border-transparent rounded-full">
          X
        </button>
      )}
    </div>
  );
}


function SelectList({ searchPlaceholder, selectedLabel, api, handleChange, subtextFormatter, defaults, hideSelections }) {
  const [options, setOptions] = useState([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searching, setSearching] = useState(false);
  const debouncedSearchTerm = useDebounce(query, 400); // delay

  const prepped = options.map(option => {
    if (selectedIds.indexOf(option.id) !== -1) {
      return {
        ...option,
        active: true,
      };
    }
    return {
      ...option
    };
  });
  // console.log(prepped, 'prepped options');

  useEffect(() => {
    // Handle any provided defaults
    if (defaults && defaults.length) {
      const newSelectedIds = defaults.map(item => item.id);
      setSelectedIds(newSelectedIds);
      setSelected([...defaults]);
    }
    fetchList(api, '', setOptions, setSearching);
  }, []);

  useEffect(() => {
    setSearching(true);
    fetchList(api, debouncedSearchTerm, setOptions, setSearching);
  }, [debouncedSearchTerm]);


  return (
    <div className="w-full py-1 mb-2">
      {/* <div className="block text-gray-700 text-lg font-semibold py-2 px-2">
        Search Colleges
          </div> */}
      <div className="flex items-center bg-gray-200 rounded-md">
        <div className="pl-2">
          <svg className="fill-current text-gray-500 w-6 h-6" xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24">
            <path className="heroicon-ui"
              d="M16.32 14.9l5.39 5.4a1 1 0 0 1-1.42 1.4l-5.38-5.38a8 8 0 1 1 1.41-1.41zM10 16a6 6 0 1 0 0-12 6 6 0 0 0 0 12z" />
          </svg>
        </div>
        <input
          className="w-full rounded-md bg-gray-50 border-l-0 border border-gray-200 text-gray-700 leading-tight focus:outline-none py-2 px-2"
          id="search" type="text" onChange={e => setQuery(e.target.value)} placeholder={searchPlaceholder} />
      </div>
      <div className="py-3 text-sm overflow-y-scroll h-64">
        {searching && <p className="px-8 py-4">Searching...</p>}
        {!searching && prepped.map(row => {
          const subtext = subtextFormatter ? subtextFormatter(row) : null;
          return (
            <SelectRow
              active={row.active}
              toggle={() => toggleSelection(row, selected, setSelected, selectedIds, setSelectedIds, handleChange)}
              name={row.name}
              subtext={subtext}
              key={`${row.id}`}
            />
          );
        })}
      </div>
      {!hideSelections && (
        <div className="block bg-gray-200 text-sm py-2 px-3 -mx-3 -mb-2">
          <div className="block text-gray-700 font-semibold py-2 px-2">
            {selectedLabel} {selected.length ? `(${selected.length})` : ''}
          </div>
          <div className="pb-3 text-sm">
            {selected.length ?
              selected.map(row => {
                const subtext = subtextFormatter ? subtextFormatter(row) : null;
                return (
                  <SelectRow
                    name={row.name}
                    toggle={e => e.preventDefault()}
                    subtext={subtext}
                    key={`${row.id}`}
                    remove={() => toggleSelection(row, selected, setSelected, selectedIds, setSelectedIds, handleChange)}
                  />
                )
              })
              : null
            }
            {!selected.length ? <p className="px-2">You haven't made any selections yet.</p> : null}
          </div>
        </div>
      )}
    </div>
  );
}



export default SelectList;